// Ledger local-day/month scope and URL contracts only; API pagination and receipt evidence stay in their existing modules.
export interface LedgerFilters {
  from: string; to: string; category_id: string; recorded_by: string; spender_user_id: string;
  q: string; status: string; kind: string;
}
const FILTER_KEYS = ['from', 'to', 'category_id', 'recorded_by', 'spender_user_id', 'q', 'status', 'kind'] as const;
const pad = (value: number) => String(value).padStart(2, '0');
export function ledgerTodayRange(today = new Date()): { from: string; to: string } {
  const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  return { from: date, to: date };
}
export function ledgerMonthRange(month: string): { from: string; to: string } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return null;
  const year = Number(match[1]), index = Number(match[2]);
  if (year < 1 || index < 1 || index > 12) return null;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][index - 1];
  return { from: `${month}-01`, to: `${month}-${days}` };
}
export function defaultLedgerFilters(today = new Date()): LedgerFilters {
  const month = `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
  return { ...ledgerMonthRange(month)!, category_id: '', recorded_by: '', spender_user_id: '', q: '', status: '', kind: '' };
}
export function readLedgerFilters(params: URLSearchParams, today = new Date()): LedgerFilters {
  const status = params.get('status') === 'pending' ? 'pending' : params.get('status') === 'verified' ? 'verified' : '';
  const explicitDates = params.has('from') || params.has('to');
  const range = explicitDates || params.get('date_scope') === 'all' || status === 'pending'
    ? { from: params.get('from') ?? '', to: params.get('to') ?? '' }
    : defaultLedgerFilters(today);
  return {
    from: range.from, to: range.to,
    category_id: params.get('category_id') ?? '', recorded_by: params.get('recorded_by') ?? '',
    spender_user_id: params.get('spender_user_id') ?? '', q: params.get('q') ?? '', status,
    kind: params.get('kind') === 'income' ? 'income' : params.get('kind') === 'expense' ? 'expense' : '',
  };
}
export function writeLedgerFilters(params: URLSearchParams, filters: LedgerFilters): URLSearchParams {
  const result = new URLSearchParams(params);
  for (const key of FILTER_KEYS) {
    if (filters[key]) result.set(key, filters[key]); else result.delete(key);
  }
  // An explicit all-date choice must survive reloads and a return from receipt details.
  if (!filters.from && !filters.to) result.set('date_scope', 'all'); else result.delete('date_scope');
  return result;
}
export function countLedgerFilters(filters: LedgerFilters, today = new Date()): number {
  const defaults = filters.status === 'pending' ? { from: '', to: '' } : defaultLedgerFilters(today);
  const dateCount = filters.from !== defaults.from || filters.to !== defaults.to ? 1 : 0;
  return dateCount + FILTER_KEYS.filter(key => key !== 'from' && key !== 'to' && filters[key] !== '').length;
}
export function ledgerMonthValue(filters: Pick<LedgerFilters, 'from' | 'to'>): string {
  const month = filters.from.slice(0, 7), range = ledgerMonthRange(month);
  return range?.from === filters.from && range.to === filters.to ? month : '';
}
export function groupLedgerReceipts<T extends { receipt_date: string | null; created_at: number }>(rows: T[]): Array<{ date: string; items: T[] }> {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const created = new Date(row.created_at * 1000);
    const fallback = Number.isFinite(created.getTime()) ? `${created.getFullYear()}-${pad(created.getMonth() + 1)}-${pad(created.getDate())}` : '';
    const date = row.receipt_date || fallback;
    const items = groups.get(date);
    if (items) items.push(row); else groups.set(date, [row]);
  }
  return Array.from(groups, ([date, items]) => ({ date, items }));
}

/** Assistant spending defaults missing dates to this month, so express open sides with the full supported ISO date domain. */
export function ledgerAssistantScope(filters: LedgerFilters): Record<string, string> {
  const scope: Record<string, string> = { from: filters.from || '0001-01-01', to: filters.to || '9999-12-31' };
  for (const key of ['category_id', 'recorded_by', 'q'] as const) if (filters[key]) scope[key] = filters[key];
  if (filters.spender_user_id) scope.user_id = filters.spender_user_id;
  return scope;
}
